import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import {
  controlPointLabel,
  fmtCoord,
  previewToSheet,
  sheetToPreview,
  snapKindLabel,
  type GeoControlPoint,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { IconCrosshair } from '@shared/ui/icons';
import { Chip, ErrorText, Muted, Num } from '@widgets/GeoSchemes';
import {
  nearestSnap,
  roundExact,
  roundToPreview,
  snapLayerPaths,
  SNAP_RADIUS_PX,
  toPreviewSnaps,
} from '../snap';
import { HotSnap } from './HotSnap';
import { LegendGlyph } from './LegendGlyph';
import {
  Accuracy,
  Canvas,
  CursorText,
  Kbd,
  Legend,
  LegendItem,
  Marker,
  Overlay,
  PickText,
  Readout,
  SnapNotes,
  Toolbar,
  Viewport,
  ZoomValue,
} from './styles';
import {
  ACCURACY_FREE,
  snapStateText,
  ZOOMS,
  type Props,
  type SheetPick,
} from './types';

/**
 * Превью проектного листа с наложенным SVG: щелчок ставит точку привязки и
 * прилипает к ближайшей точке векторной графики листа (пересечение осей, центр
 * сваи, блок) в радиусе ~14 экранных пикселей; с Alt или выключенным
 * «Прилипанием» — точно туда, куда щёлкнули. Маркер можно перетащить (тоже с
 * прилипанием) или сдвинуть стрелками (Shift — шаг ×10). Пиксель превью
 * пересчитывается в координаты листа по `px_per_unit` и `extents`.
 */
export function SheetPointPicker({
  sheet,
  url,
  loadError,
  snaps,
  points,
  selectedId,
  editable,
  onAdd,
  onMove,
  onSelect,
}: Props) {
  const [zoomIdx, setZoomIdx] = useState(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragId = useRef<string | null>(null);
  const dragPick = useRef<SheetPick | null>(null);
  const overSheet = useRef(false);
  const [renderedWidth, setRenderedWidth] = useState(0);
  const [snapOn, setSnapOn] = useState(true);
  const [alt, setAlt] = useState(false);
  const [cursor, setCursor] = useState<{ px: number; py: number } | null>(null);
  const [last, setLast] = useState<{ id: string; pick: SheetPick } | null>(null);

  const pw = sheet.preview_width_px;
  const ph = sheet.preview_height_px;
  const zoom = ZOOMS[zoomIdx] ?? 1;

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setRenderedWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Пикселей превью на один CSS-пиксель: маркеры, подписи и радиус прилипания —
  // постоянного экранного размера при любом зуме.
  const k = renderedWidth > 0 ? pw / renderedWidth : 1;

  const previewSnaps = useMemo(() => toPreviewSnaps(sheet, snaps.points), [sheet, snaps.points]);
  const snapping = editable && snapOn && previewSnaps.length > 0;
  const layer = useMemo(
    () => (snapping ? snapLayerPaths(previewSnaps, k) : null),
    [snapping, previewSnaps, k],
  );

  // Alt переключает режим и без движения мыши. Отпускание Alt над листом не
  // отдаём браузеру: в Windows оно уводит фокус в меню окна.
  useEffect(() => {
    if (!snapping) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Alt') return;
      setAlt(e.type === 'keydown');
      if (overSheet.current) e.preventDefault();
    };
    const release = () => setAlt(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('blur', release);
    };
  }, [snapping]);

  function toPreview(clientX: number, clientY: number): { px: number; py: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    const px = ((clientX - r.left) / r.width) * pw;
    const py = ((clientY - r.top) / r.height) * ph;
    return { px: Math.min(Math.max(px, 0), pw), py: Math.min(Math.max(py, 0), ph) };
  }

  /** Точка листа для пикселя превью: прилипшая к графике или «по картинке». */
  function resolve(px: number, py: number, free: boolean): { pick: SheetPick; hot: number | null } {
    if (snapping && !free) {
      const i = nearestSnap(previewSnaps, px, py, SNAP_RADIUS_PX * k);
      const s = i == null ? undefined : previewSnaps[i];
      if (i != null && s) {
        return { pick: { u: roundExact(s.snap.x), v: roundExact(s.snap.y), snap: s.snap }, hot: i };
      }
    }
    const { u, v } = previewToSheet(sheet, px, py);
    return {
      pick: {
        u: roundToPreview(u, sheet.px_per_unit),
        v: roundToPreview(v, sheet.px_per_unit),
        snap: null,
      },
      hot: null,
    };
  }

  const hover = cursor ? resolve(cursor.px, cursor.py, alt) : null;
  const hot = hover?.hot != null ? previewSnaps[hover.hot] : undefined;

  function track(e: PointerEvent<SVGSVGElement>) {
    overSheet.current = true;
    setAlt(e.altKey);
    setCursor(toPreview(e.clientX, e.clientY));
  }

  function leave() {
    overSheet.current = false;
    setCursor(null);
  }

  function handleAdd(e: MouseEvent<SVGRectElement>) {
    if (!editable) return;
    const p = toPreview(e.clientX, e.clientY);
    if (!p) return;
    const { pick } = resolve(p.px, p.py, e.altKey);
    // В этом месте уже есть точка (та же точка графики) — выбрать её, а не плодить дубль.
    const tol = sheet.px_per_unit > 0 ? 0.5 / sheet.px_per_unit : 0;
    const twin = points.find(
      (cp) => Math.abs(cp.sheet_x - pick.u) <= tol && Math.abs(cp.sheet_y - pick.v) <= tol,
    );
    if (twin) {
      onSelect(twin.id);
      return;
    }
    const id = onAdd(pick);
    setLast({ id, pick });
  }

  function startDrag(e: PointerEvent<SVGGElement>, id: string) {
    e.stopPropagation();
    onSelect(id);
    if (!editable) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragId.current = id;
    dragPick.current = null;
  }

  function drag(e: PointerEvent<SVGGElement>, id: string) {
    if (dragId.current !== id) return;
    const p = toPreview(e.clientX, e.clientY);
    if (!p) return;
    const { pick } = resolve(p.px, p.py, e.altKey);
    dragPick.current = pick;
    onMove(id, pick);
  }

  function endDrag(e: PointerEvent<SVGGElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const id = dragId.current;
    const pick = dragPick.current;
    if (id && pick) setLast({ id, pick });
    dragId.current = null;
    dragPick.current = null;
  }

  function nudge(e: KeyboardEvent<SVGGElement>, p: GeoControlPoint) {
    const step = e.shiftKey ? 10 : 1;
    let dx = 0;
    let dy = 0;
    if (e.key === 'ArrowLeft') dx = -step;
    else if (e.key === 'ArrowRight') dx = step;
    else if (e.key === 'ArrowUp') dy = -step;
    else if (e.key === 'ArrowDown') dy = step;
    else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(p.id);
      return;
    } else return;
    e.preventDefault();
    if (!editable) return;
    const cur = sheetToPreview(sheet, p.sheet_x, p.sheet_y);
    const pick = resolve(cur.px + dx, cur.py + dy, true).pick;
    onMove(p.id, pick);
    setLast({ id: p.id, pick });
  }

  const placed = points.filter((p) => Number.isFinite(p.sheet_x) && Number.isFinite(p.sheet_y));
  const shownLast = last && points.some((p) => p.id === last.id) ? last : null;
  const stateText = editable ? snapStateText(snaps, previewSnaps.length, snapOn) : null;

  return (
    <div>
      <Toolbar $gap={2}>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
          disabled={zoomIdx === 0}
          aria-label="Уменьшить"
        >
          −
        </Button>
        <ZoomValue aria-live="polite">{Math.round(zoom * 100)}%</ZoomValue>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => setZoomIdx((i) => Math.min(ZOOMS.length - 1, i + 1))}
          disabled={zoomIdx === ZOOMS.length - 1}
          aria-label="Увеличить"
        >
          +
        </Button>
        <Button
          type="button"
          $variant="ghost"
          $size="sm"
          onClick={() => setZoomIdx(0)}
          disabled={zoomIdx === 0}
        >
          Вписать
        </Button>
        {editable && (
          <Chip
            type="button"
            $active={snapOn && previewSnaps.length > 0}
            aria-pressed={snapOn}
            disabled={previewSnaps.length === 0}
            onClick={() => setSnapOn((on) => !on)}
          >
            <IconCrosshair size={14} /> Прилипание к графике
          </Chip>
        )}
      </Toolbar>
      <Viewport>
        <Canvas ref={canvasRef} $zoom={zoom}>
          {url ? (
            <img src={url} alt="Проектный лист для привязки" draggable={false} />
          ) : (
            <Muted>{loadError ? '' : 'Загрузка превью…'}</Muted>
          )}
          {url && (
            <Overlay
              ref={svgRef}
              viewBox={`0 0 ${pw} ${ph}`}
              preserveAspectRatio="none"
              $editable={editable}
              role="group"
              aria-label="Точки привязки на листе"
              onPointerMove={editable ? track : undefined}
              onPointerLeave={editable ? leave : undefined}
            >
              <rect x={0} y={0} width={pw} height={ph} fill="transparent" onClick={handleAdd} />
              {layer && (
                <g className={alt ? 'snaps free' : 'snaps'} aria-hidden="true">
                  {layer.strokes && (
                    <>
                      <path className="snap-halo" d={layer.strokes} />
                      <path className="snap-line" d={layer.strokes} />
                    </>
                  )}
                  {layer.dots && <path className="snap-dot" d={layer.dots} />}
                </g>
              )}
              {hot && <HotSnap s={hot} k={k} flip={hot.px > pw * 0.7} />}
              {placed.map((p) => {
                const { px, py } = sheetToPreview(sheet, p.sheet_x, p.sheet_y);
                const r = 7 * k;
                const arm = 13 * k;
                const name = controlPointLabel(p.id);
                return (
                  <Marker
                    key={p.id}
                    $selected={p.id === selectedId}
                    tabIndex={0}
                    role="button"
                    aria-label={`Точка ${name}${editable ? ', стрелки — сдвинуть' : ''}`}
                    aria-pressed={p.id === selectedId}
                    onPointerDown={(e) => startDrag(e, p.id)}
                    onPointerMove={(e) => drag(e, p.id)}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => nudge(e, p)}
                  >
                    <circle className="focus" cx={px} cy={py} r={r + 5 * k} />
                    <circle className="ring" cx={px} cy={py} r={r} />
                    <path
                      className="cross"
                      d={`M ${px - arm} ${py} H ${px + arm} M ${px} ${py - arm} V ${py + arm}`}
                    />
                    <text className="label" x={px + r + 4 * k} y={py - r - 2 * k} fontSize={12 * k}>
                      {name}
                    </text>
                  </Marker>
                );
              })}
            </Overlay>
          )}
        </Canvas>
      </Viewport>

      {editable && url && (
        <Readout>
          <PickText role="status">
            {shownLast ? (
              <>
                <b>{controlPointLabel(shownLast.id)}</b> →{' '}
                {shownLast.pick.snap ? snapKindLabel(shownLast.pick.snap) : 'без прилипания'}: u{' '}
                <Num>{fmtCoord(shownLast.pick.u, 2)}</Num>, v{' '}
                <Num>{fmtCoord(shownLast.pick.v, 2)}</Num> ·{' '}
                <Accuracy $exact={shownLast.pick.snap != null}>
                  {shownLast.pick.snap ? 'по графике листа — точно' : ACCURACY_FREE}
                </Accuracy>
              </>
            ) : (
              'Щелчок по листу — новая точка привязки; маркер можно перетащить.'
            )}
          </PickText>
          {hover && (
            <CursorText aria-hidden="true">
              {snapping && alt
                ? 'Alt — без прилипания · '
                : hover.pick.snap
                  ? `${snapKindLabel(hover.pick.snap)} · `
                  : ''}
              u {fmtCoord(hover.pick.u, 2)} v {fmtCoord(hover.pick.v, 2)}
            </CursorText>
          )}
        </Readout>
      )}

      {editable && url && (
        <Legend>
          {stateText ?? (
            <>
              <LegendItem>
                <LegendGlyph glyph="cross" /> пересечение осей
              </LegendItem>
              <LegendItem>
                <LegendGlyph glyph="x" /> пересечение линий
              </LegendItem>
              <LegendItem>
                <LegendGlyph glyph="square" /> центр сваи
              </LegendItem>
              <LegendItem>
                <LegendGlyph glyph="dot" /> блок, окружность, точка
              </LegendItem>
              <span>
                <Kbd>Alt</Kbd> + щелчок — без прилипания
              </span>
              <span>Точность: по графике листа — точно; {ACCURACY_FREE}.</span>
            </>
          )}
        </Legend>
      )}
      {editable && snaps.warnings.length > 0 && (
        <SnapNotes>
          {snaps.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </SnapNotes>
      )}

      {loadError && (
        <ErrorText role="alert">
          Превью листа не загрузилось — точки можно ввести в таблице.
        </ErrorText>
      )}
    </div>
  );
}
